import React from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';

const Tag = ({ children }) => (
  <span className="inline-flex items-center rounded-lg bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 px-2.5 py-1 text-xs font-medium">
    {children}
  </span>
);

const formatDate = (iso) => {
  // Parse as local date to avoid timezone shifting
  const [y, m, d] = (iso || '').split('-').map(Number);
  const date = new Date(y, (m || 1) - 1, d || 1);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export default function ChangelogIndex() {
  const [entries, setEntries] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;
    fetch('/changelog/index.json', { cache: 'no-store' })
      .then((r) => {
        if (!r.ok) throw new Error('Failed to load changelog index');
        return r.json();
      })
      .then((data) => {
        if (!mounted) return;
        const sorted = [...data].sort((a, b) => (a.date < b.date ? 1 : -1));
        setEntries(sorted);
        setLoading(false);
      })
      .catch((e) => {
        if (!mounted) return;
        setError(e.message);
        setLoading(false);
      });
    return () => (mounted = false);
  }, []);

  return (
    <div className="pt-28 pb-16 px-6">
      <SEO
        title="Changelog – Boxia"
        description="Latest updates, improvements, and fixes from the Boxia platform."
        url={typeof window !== 'undefined' ? window.location.href : ''}
      />
      <div className="max-w-5xl mx-auto">
        <header className="mb-8">
          <div className="inline-block rounded-2xl border border-black/5 dark:border-white/10 bg-white/60 dark:bg-gray-900/50 backdrop-blur px-4 py-2 text-xs font-medium text-gray-600 dark:text-gray-300">
            Product updates
          </div>
          <h1 className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900 dark:text-gray-100">
            Changelog
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            The latest shipped features, improvements, and fixes — from newest to oldest.
          </p>
        </header>

        {loading && (
          <div className="text-gray-500 dark:text-gray-400">Loading updates…</div>
        )}
        {error && (
          <div className="text-red-600 dark:text-red-400">{error}</div>
        )}

        <div className="space-y-4">
          {entries.map((item) => (
            <Link
              key={item.slug}
              to={`/changelog/${item.slug}`}
              className="block group"
            >
              <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-white/70 dark:bg-gray-900/60 backdrop-blur hover:bg-white/90 dark:hover:bg-gray-900 transition-colors p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDate(item.date)}
                    </div>
                    <h2 className="mt-1 text-lg md:text-xl font-semibold text-gray-900 dark:text-gray-100 group-hover:text-purple-600 dark:group-hover:text-purple-300">
                      {item.title}
                    </h2>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                      {item.summary}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {(item.tags || []).slice(0, 3).map((tag) => (
                      <Tag key={tag}>{tag}</Tag>
                    ))}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
